<script>
   /**************************************************************/
   /* PLSDASettings                                              */
   /* -------------------------------------                      */
   /* shows panel with PLS-DA model settings.                    */
   /**************************************************************/

   import { onDestroy } from 'svelte';
   import { Select, Container, Range } from 'svelte-controls-basic';
   import { Vector } from 'mdatools/arrays';
   import { median } from 'mdatools/stat';
   import { qnorm } from 'mdatools/distributions';
   import { plsres_get } from '@mdatools-apps/wasm/pls.js';
   import { genhash } from '@mdatools-apps/shared/utils';

   import { daState } from './states.svelte.js';

   let {
      plsmodel,
      plsres,
      ncomp,
      ind,
      classNames,
      damodel = $bindable(),
      keepSettings = false,
   } = $props();

   // prefix for the label ids the controls below are named by. it must be unique on the page and
   // every tab of the app stays mounted, so it can not be written out by hand
   const uid = $props.id();

   function getParams(plsres, ind) {

      const nclasses = classNames.length;
      const params = {};

      for (let c = 0; c < nclasses; c++) {

         const cname = classNames[c]
         const indc = ind[cname];
         const nc = indc.length;

         const mc = new Array(plsres.ncomp);
         const sc = new Array(plsres.ncomp);
         const mr = new Array(plsres.ncomp);
         const sr = new Array(plsres.ncomp);

         for (let a = 1; a <= plsres.ncomp; a++) {

            const yp = plsres_get(plsres, "Yp", a, 1);
            const ypc = Vector.zeros(nc);

            // compute mean and save all class members to separate vector
            let mca = 0.;
            let j = 0;
            for (let i of indc) {
               mca += yp[i];
               ypc.v[j] = yp[i];
               j += 1;
            }
            mca = mca / nc;

            // compute median
            const mra = median(ypc);

            // compute sd
            let sca = 0;
            let sra = 0;
            j = 0;
            const MDA = Vector.zeros(nc);
            for (let i of indc) {
               sca += (yp[i] - mca)**2;
               MDA.v[j] = Math.abs((yp[i] - mra));
               j += 1;
            }

            sca = Math.sqrt(sca / (nc - 1));
            sra = 1.4826 * median(MDA);

            // save to vectors
            mc[a - 1] = mca;
            sc[a - 1] = sca;
            mr[a - 1] = mra;
            sr[a - 1] = sra;
         }

         params[cname] = {'classic': {m: mc, s:sc}, 'robust': {m:mr, s:sr}};
      }

      return params;
   }


   // initialize SIMCA object with default values
   $effect(() => {
      if (damodel || !plsmodel || !plsres || !ind) return;
      const alpha = daState.alpha;
      const gamma = daState.gamma;
      const threshold = daState.threshold;
      const rule = daState.rule;
      const limType = daState.limType;
      const params = getParams(plsres, ind);
      const dof = classNames.map(v => ind[v].length);

      damodel = {
         ncomp,
         rule,
         limType,
         threshold,
         alpha,
         gamma,
         classNames: [...classNames],
         params,
         ind,
         crit: null,
         dof: dof,
         hash: genhash()
      };
   });

   const p = $derived(damodel && damodel.limType ?
      [
         damodel.params[damodel.classNames[0]][damodel.limType],
         damodel.params[damodel.classNames[1]][damodel.limType],
      ] : null);

   $effect(() => {
      if (!p || damodel.rule === 'hard') return;

      // computing critical limits for plot
      if (damodel.ncomp) {
         const alpha = Number.parseFloat(damodel.alpha) / 100;
         const m1 = p[0].m[damodel.ncomp - 1];
         const m2 = p[1].m[damodel.ncomp - 1];
         const s1 = p[0].s[damodel.ncomp - 1];
         const s2 = p[1].s[damodel.ncomp - 1];
         const crit1 = qnorm(alpha, m1, s1);
         const crit2 = qnorm(1 - alpha, m2, s2);

         const gamma = Number.parseFloat(damodel.gamma) / 100;
         const alpha_out1 = (1 - Math.pow(1 - gamma, 1/damodel.dof[0]))
         const alpha_out2 = (1 - Math.pow(1 - gamma, 1/damodel.dof[1]))
         const out1 = qnorm(alpha_out1, m1, s1);
         const out2 = qnorm(1 - alpha_out2, m2, s2);

         const crit = {}
         crit[damodel.classNames[0]] = [m1, s1, crit1, out1];
         crit[damodel.classNames[1]] = [m2, s2, crit2, out2];
         damodel.crit = crit;
      }
   });

  // only the train tab keeps the settings for its next model; a test tab has those of the model file
  onDestroy(() => {
      if (keepSettings && damodel) {
         daState.alpha = damodel.alpha;
         daState.gamma = damodel.gamma;
         daState.threshold = damodel.threshold;
         daState.rule = damodel.rule;
         daState.limType = damodel.limType;
      }
  });
</script>

{#if damodel}
<Container name="ncomp" label="# of LVs" id="{uid}-ncomp">
   <Range bind:value={damodel.ncomp} min={1} max={plsmodel.ncomp} step={1} decNum={0} />
</Container>

<Container name="rule" label='Rule' id="{uid}-rule">
   <Select bind:value={damodel.rule} options={['hard', 'soft']} />
</Container>

   {#if damodel.rule === "hard"}
      <Container name="threshold" label="Threshold" id="{uid}-threshold">
         <Range bind:value={damodel.threshold} min={-1} max={1} step={0.02} decNum={2} />
      </Container>
   {:else}
      <Container name="alpha" label='Alpha, %' id="{uid}-alpha">
         <Range bind:value={damodel.alpha} min={1} max={50} step={1} decNum={0} />
      </Container>
      <Container name="limits" label='Limits' id="{uid}-limits">
         <Select bind:value={damodel.limType} options={['classic', 'robust']} />
      </Container>
      <Container name="gamma" label='Gamma, %' id="{uid}-gamma">
         <Select bind:value={damodel.gamma} options={[10, 5, 1, 0.1, 0.01]} />
      </Container>
   {/if}
{/if}
